import { useCallback, useMemo, useRef, useState } from 'react'
import qs from 'qs'
import Title from '../common/Title'
import { useIntl } from 'react-intl'
import Card from '../common/Card'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { getCurrentValues, partnerItemOnOperation } from '../../utils/operationUtils'
import { getPointsColumns } from './components/pointsColumns'
import { createDateFromISO } from 'utils/dates'
import Filters, { OperationFilterForm } from './components/Filters'
import { getOperationExportList } from '../../api/operation'
import { useMutation } from '@tanstack/react-query'
import { useAppSelector } from '../../store/hooks'
import { useGetProgramFilter } from '../../hooks/useGetProgramFilter'
import TableOperations from './components/TableOperations'
import { toast } from 'react-toastify'
import { Amount, Button } from 'ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faDownload, faShareFromSquare } from '@fortawesome/free-solid-svg-icons'
import ResumeOperations from './components/ResumeOperations'
import classNames from 'classnames'

export type Operation = {
   id: string
   reservationName: string
   reservationCode: string
   reservationTotal: number
   reservationDate: string
   currency: string
   points: number
   pointsAccumulated: number | null
   pointsRedeemed: number | null
   member: partnerItemOnOperation
   operationType: string
   operationStatus: string
   loyaltyPlanId: number
   levelName: string
   serviceType: string
   serviceTypeName: string
   importId: string
   roomNights: number
   channel: string
   applicationDate: string
   createdAt: string
   amount: number
   isCertificate: boolean
   certificateDetail?: string
   registrationChannelName?: number
   registrationChannel?: string
   roomsPerNight?: number
   numberOfNights?: number
   reference?: string
   invoiceNumber?: string
   hotelName?: string
   reservationUuid?: string
   registationChannel?: number
   operationDate: string
   pointsRemaining?: number
   pointsExpirationDate?: string
   expirationColor?: 'RED' | 'ORANGE' | null
   expirationLabel?: string
}

type Props = {}

export const Operations = (props: Props) => {
   const intl = useIntl()
   const { locale } = useAppSelector((state) => state.base)
   const navigate = useNavigate()
   const location = useLocation()
   const { accountCode } = useParams<{ accountCode: string }>()
   const { data: programs, isSuccess } = useGetProgramFilter()
   const [isExporting, setIsExporting] = useState(false)
   const divRef = useRef<HTMLDivElement>(null)
   const { mutate: exportFile } = useMutation({
      mutationFn: getOperationExportList,
   })
   const defaultProgram =
      programs && programs.items.length > 1
         ? programs.items.find((program) => program.isDefault)?.id
         : programs?.items?.[0]?.id

   const currentValues = useCallback(
      () => getCurrentValues(location.search, defaultProgram),
      [location.search, defaultProgram],
   )()
   let loyaltyPlan = programs?.items?.find(
      (plan) => plan.id === Number(currentValues.loyaltyPlanIds) || (defaultProgram && plan.id === defaultProgram),
   )
   const handleCopy = () => {
      if (divRef.current) {
         const textToCopy = divRef.current.textContent || ''
         navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
               toast.info('Texto copiado al portapapeles: ')
            })
            .catch((err) => {
               toast.info('Error al copiar el texto: ')
            })
      }
   }
   const columns = useMemo(
      () => [
         {
            header: intl.formatMessage({ id: 'operations.table.operation.id' }),
            accessorKey: 'importId',
             size: 90,
             minSize: 80,
            cell: ({ row }: { row: { original: Operation } }) => {
               return (
                  <Link to={`/${accountCode}/partners/${row.original.member.id}/operation/${row.original.id}`}>
                     {row.original.id ? row.original.importId : '--'}
                  </Link>
               )
            },
            enableSorting: true,
         },
         {
            header: intl.formatMessage({ id: 'operations.table.operation.partner' }),
            accessorKey: 'fullName',
            cell: ({ row }: { row: { original: Operation } }) => {
               return (
                  <Link to={`/${accountCode}/partners/${row.original.member.id}/operations`}>
                     {row.original.member.fullName}
                     <div className="text-sm text-neutral-400">{row.original.member.email}</div>
                  </Link>
               )
            },
            enableSorting: true,
         },
         {
            header: () => <div> {intl.formatMessage({ id: 'operations.table.partner.amount' })}</div>,
            accessorKey: 'amount',
            cell: ({ row }: { row: { original: Operation } }) => {
               return (
                  <div ref={divRef} onClick={handleCopy}>
                     {row.original.amount !== null ? (
                        <Amount amount={row.original.amount} minimumFractionDigits={2} maximumFractionDigits={2} />
                     ) : (
                        '--'
                     )}
                  </div>
               )
            },
            enableSorting: true,
         },
         {
            header: () => <div>{intl.formatMessage({ id: 'operations.table.partner.id' })}</div>,
            accessorKey: 'externalId',
            cell: ({ row }: { row: { original: Operation } }) => {
               return (
                  <div ref={divRef} className="cursor-pointer" onClick={handleCopy}>
                     {row.original.member.externalId ? row.original.member.externalId : '--'}
                  </div>
               )
            },
            enableSorting: true,
         },
         ...(loyaltyPlan?.allowPoints
            ? [
                 {
                    header: intl.formatMessage({ id: 'operations.table.generic.type' }),
                    accessorKey: 'serviceType',
                    cell: ({ row }: { row: { original: Operation } }) => {
                       return (
                          <div>
                             <div>{row.original.serviceType}</div>
                             {row.original.reservationCode ? (
                                <Link
                                   to={`${process.env.REACT_APP_CRS_URL}/#/${accountCode}/itineraries/${row.original.reservationUuid}`}
                                   className="text-blue-600 dark:text-blue-500 hover:underline text-sm"
                                   target="_blank"
                                >
                                   {row.original.reservationCode}
                                </Link>
                             ) : null}
                          </div>
                       )
                    },
                    enableSorting: true,
                 },
                 {
                    header: () => <div>{intl.formatMessage({ id: 'operations.table.type' })}</div>,
                    accessorKey: 'operationType',
                    cell: ({ row }: { row: { original: Operation } }) => {
                       return (
                          <div>
                             {row.original.operationType
                                ? intl.formatMessage({ id: `operations.table.type.${row.original.operationType}` })
                                : '--'}
                             <div
                                className={classNames('text-sm', {
                                   'text-red-600': row.original.operationStatus === 'CANCELLED',
                                   'text-neutral-400': row.original.operationStatus !== 'CANCELLED',
                                })}
                             >
                                {row.original.operationStatus
                                   ? intl.formatMessage({ id: `operations.table.type.${row.original.operationStatus}` })
                                   : '--'}
                             </div>
                          </div>
                       )
                    },
                    enableSorting: true,
                 },
                 ...getPointsColumns(intl),
              ]
            : []),

         {
            header: () => <div>{intl.formatMessage({ id: 'operations.table.CN' })}</div>,
            accessorKey: 'roomNights',
            minSize: 110,
            cell: ({ row }: { row: { original: Operation } }) => {
               return <div>{row.original.roomNights}</div>
            },
            enableSorting: true,
         },
         {
            header: intl.formatMessage({ id: 'operations.table.date.stay' }),
            accessorKey: 'reservationDate',
            cell: ({ row }: { row: { original: Operation } }) => {
               const isoDate = createDateFromISO(row.original.reservationDate, { setZone: true }).setLocale(locale)
               return <div>{isoDate.isValid ? isoDate.toFormat(intl.formatMessage({ id: 'date.format' })) : '--'}</div>
            },
            enableSorting: true,
         },
         {
            header: intl.formatMessage({ id: 'operations.table.date.application' }),
            accessorKey: 'applicationDate',
            cell: ({ row }: { row: { original: Operation } }) => {
               const isoDate = createDateFromISO(row.original.applicationDate).setLocale(locale)
               return (
                  <div>
                     {isoDate.toFormat(intl.formatMessage({ id: 'date.format' }))}
                     <div className="text-sm text-neutral-400">{`${isoDate.toFormat('h:mm a')}`}</div>
                  </div>
               )
            },
            enableSorting: true,
         },
         ...(loyaltyPlan?.allowPoints
            ? [
                 {
                    header: intl.formatMessage({ id: 'operations.table.registrationChannel' }),
                    accessorKey: 'channel',
                    cell: ({ row }: { row: { original: Operation } }) => {
                       return <div>{row.original.channel}</div>
                    },
                    enableSorting: true,
                 },
              ]
            : []),
      ],
      [intl, locale, accountCode, loyaltyPlan?.allowPoints],
   )
   const onFilter = (data: OperationFilterForm) => {
      const request = Object.assign({}, data)
      navigate(
         `?${qs.stringify(
            { ...request, page: 0 },
            {
               arrayFormat: 'comma',
               allowDots: true,
               encode: false,
            },
         )}`,
      ) //TODO: revisar si se soluciona el bug que encodea la coma https://github.com/ljharb/qs/issues/410
   }

   const onPageChange = (page: { page: number; size: number }) => {
      const request = Object.assign({}, currentValues, { ...page })
      navigate(
         `?${qs.stringify(request, {
            arrayFormat: 'comma',
            allowDots: true,
            encode: false,
         })}`,
      )
   }
   const onSort = (sort: () => Array<{ id: string; desc: boolean }>) => {
      const sortField = sort()?.flat()?.[0]
      if (!sortField.id) return
      const { sort: sortFilter, from, to, ...currentValues } = getCurrentValues(location.search)
      const [currentSortField, currentSortOrder] = sortFilter?.split(',') || []
      let newSort = null

      if (currentSortField !== sortField.id) {
         newSort = `${sortField.id},asc`
      } else if (currentSortOrder === 'asc') {
         newSort = `${sortField.id},desc`
      } else if (currentSortOrder === 'desc') {
         newSort = null
      }

      const request = Object.assign(
         {},
         currentValues,
         from && to
            ? {
                 dates: {
                    startDate: from,
                    endDate: to,
                 },
              }
            : {},
         newSort ? { sort: newSort } : {},
      )
      navigate(
         `?${qs.stringify(request, {
            arrayFormat: 'comma',
            allowDots: true,
            encode: false,
         })}`,
      )
   }

   const onExport = () => {
      const toastId = toast.loading('Generando archivo espere un momento......', { autoClose: false })
      const { loyaltyPlanIds, query, operationStatus, from, to, channelIds, operationType, sort } = currentValues

      let request = {
         account: accountCode || '',
         emailRecipients: [],
         filters: {
            ...(query ? { query: query } : {}),
            ...(loyaltyPlanIds.length ? { loyaltyPlanId: Number(loyaltyPlanIds) } : { loyaltyPlanId: defaultProgram }),
            ...(operationStatus.length ? { operationStatus: operationStatus } : {}),
            ...(from && to ? { from: from, to: to } : {}),
            ...(channelIds.length ? { channelIds: channelIds } : {}),
            ...(operationType?.length ? { operationType: operationType.join(',') } : {}),
            ...(sort && sort.length > 1 ? { sort: sort } : {}),
         },
      }
      setIsExporting(true)
      exportFile(request, {
         onSuccess: (response) => {
            let excellName = `operations.xlsx`
            const blob = new Blob([response], {
               type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = excellName
            document.body.appendChild(link)
            toast.update(toastId, {
               render: `Exportación completada 🎉`,
               type: 'success',
               isLoading: false,
               autoClose: 3000,
               position: 'bottom-center',
            })
            link.click()
            document.body.removeChild(link)
            setIsExporting(false)
         },
         onError: (error: any) => {
            setIsExporting(false)
            let errorMessage = 'Error en la exportación 😢'
            const data = error?.response?.data
            if (data instanceof ArrayBuffer) {
               try {
                  const decoded = JSON.parse(new TextDecoder().decode(data))
                  errorMessage = decoded?.error || decoded?.message || errorMessage
               } catch {}
            } else if (data?.error) {
               errorMessage = data.error
            }
            toast.update(toastId, {
               render: errorMessage,
               type: 'error',
               isLoading: false,
               autoClose: 3000,
            })
         },
      })
   }

   const onImportPms = () => {
      navigate('importPms')
   }
   return (
      <>
         <Title
            className="flex justify-between gap-x-2  items-center "
            value={intl.formatMessage({ id: 'titles.operations' })}
         ></Title>
         <div className="flex flex-col gap-y-4 mx-4">
            <Card className="py-4">
               {isSuccess && programs.items && programs.items.length > 0 && (
                  <>
                     <Filters
                        className="px-4"
                        onSubmit={onFilter}
                        defaultValues={currentValues}
                        allowPoints={loyaltyPlan?.allowPoints}
                     />
                     <div>
                        <ResumeOperations
                           request={{
                              loyaltyPlanId: Number(currentValues.loyaltyPlanIds),
                              dates: {
                                 from: currentValues.from,
                                 to: currentValues.to,
                              },
                              operationStatus: currentValues.operationStatus,
                              channelIds: currentValues.channelIds,
                              query: currentValues.query,
                           }}
                           hasPointsBenefit={loyaltyPlan?.allowPoints}
                        />
                     </div>

                     <TableOperations
                        showPointsHelp={loyaltyPlan?.allowPoints}
                        currentValues={currentValues}
                        onPageChange={onPageChange}
                        columns={columns}
                        defaultProgram={defaultProgram}
                        onSortChange={onSort}
                     >
                        <div className="flex justify-between gap-x-2">
                           <Button onClick={onImportPms} className={'flex gap-x-2 items-center text-[12px]'}>
                              <FontAwesomeIcon icon={faDownload} className={'text-gray-500 text '} />
                              {intl.formatMessage({ id: 'operations.import.PMS' })}
                           </Button>
                           <Button
                              onClick={onExport}
                              disabled={isExporting}
                              className={'flex gap-x-2 items-center text-[12px]'}
                           >
                              {isExporting ? (
                                 <FontAwesomeIcon icon={faArrowsRotate} className={'text-gray-500 text '} />
                              ) : (
                                 <FontAwesomeIcon icon={faShareFromSquare} className={'text-gray-500 text '} />
                              )}
                              {intl.formatMessage({ id: 'operations.export' })}
                           </Button>
                        </div>
                     </TableOperations>
                  </>
               )}
            </Card>
         </div>
      </>
   )
}
